// // ajay:code all 11-08-26
// import { useState } from "react";
// import { Lock } from "lucide-react";
// import { useAuth } from "../context/AuthContext";
// import { clientAccessAPI } from "../services/api";

// const unlockKey = (companyId) => `wqa_unlocked_${companyId}`;

// export function isUnlocked(companyId) {
//   return sessionStorage.getItem(unlockKey(companyId)) === "true";
// }
// export function clearUnlock(companyId) {
//   sessionStorage.removeItem(unlockKey(companyId));
// }

// /**
//  * Wraps a client-side page. If `pageKey` is in the active company's
//  * protected_pages list, shows a password prompt until verified (unlock is
//  * session-scoped to this company — cleared whenever the client visits any
//  * unprotected page, per the confirmed UX rule).
//  */
// export default function PasswordGate({ pageKey, children }) {
//   const { activeCompany } = useAuth();
//   const [password, setPassword] = useState("");
//   const [error, setError] = useState("");
//   const [checking, setChecking] = useState(false);
//   const [unlocked, setUnlocked] = useState(
//     activeCompany ? isUnlocked(activeCompany.company_id) : false,
//   );

//   if (!activeCompany) return children; // no company context yet — nothing to gate

//   const isProtected = (activeCompany.protected_pages || []).includes(pageKey);
//   if (!isProtected || unlocked) return children;

//   const submit = async (e) => {
//     e.preventDefault();
//     setError("");
//     setChecking(true);
//     try {
//       await clientAccessAPI.verifyAccess(activeCompany.company_id, password);
//       sessionStorage.setItem(unlockKey(activeCompany.company_id), "true");
//       setUnlocked(true);
//     } catch (err) {
//       setError(err.data?.message || "Incorrect password");
//     } finally {
//       setChecking(false);
//     }
//   };

//   return (
//     <div
//       style={{
//         minHeight: "60vh",
//         display: "flex",
//         alignItems: "center",
//         justifyContent: "center",
//         padding: 28,
//       }}
//     >
//       <div
//         className="card"
//         style={{
//           maxWidth: 360,
//           width: "100%",
//           textAlign: "center",
//           padding: 32,
//         }}
//       >
//         <div
//           style={{
//             width: 52,
//             height: 52,
//             borderRadius: "50%",
//             background: "var(--accent-dim)",
//             border: "1px solid var(--accent)",
//             display: "flex",
//             alignItems: "center",
//             justifyContent: "center",
//             margin: "0 auto 16px",
//           }}
//         >
//           <Lock size={22} color="var(--accent)" />
//         </div>
//         <h3 style={{ fontSize: 16, fontWeight: 700, marginBottom: 4 }}>
//           Protected Page
//         </h3>
//         <p style={{ fontSize: 13, color: "var(--text2)", marginBottom: 20 }}>
//           Enter the access password provided by {activeCompany.company_name} to
//           continue.
//         </p>
//         <form onSubmit={submit}>
//           <input
//             type="password"
//             autoFocus
//             value={password}
//             onChange={(e) => setPassword(e.target.value)}
//             placeholder="Access password"
//             style={{ marginBottom: 12, textAlign: "center" }}
//           />
//           {error && (
//             <div
//               style={{ color: "var(--danger)", fontSize: 12, marginBottom: 12 }}
//             >
//               {error}
//             </div>
//           )}
//           <button
//             type="submit"
//             className="btn-primary"
//             disabled={checking}
//             style={{ width: "100%" }}
//           >
//             {checking ? "Verifying..." : "Unlock"}
//           </button>
//         </form>
//       </div>
//     </div>
//   );
// }

// Every protected page always asks for the password, every single visit —
// no caching, no sharing across pages, no session persistence. Local
// component state only: React remounts this component fresh every time the
// route changes, so `unlocked` always starts false again. This is
// intentional — do NOT reintroduce sessionStorage/localStorage here, it
// previously caused one page's unlock to leak into every other protected
// page for the same company (all pages shared one company-scoped key).
import { useState } from "react";
import { Lock } from "lucide-react";
import { useAuth } from "../context/AuthContext";
import { clientAccessAPI } from "../services/api";

export default function PasswordGate({ pageKey, children }) {
  const { activeCompany } = useAuth();
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [checking, setChecking] = useState(false);
  const [unlocked, setUnlocked] = useState(false); // always starts locked on every mount

  if (!activeCompany) return children; // no company context yet — nothing to gate

  const pagesList = Array.isArray(activeCompany.protected_pages)
    ? activeCompany.protected_pages
    : (() => {
        try {
          const v = JSON.parse(activeCompany.protected_pages);
          return Array.isArray(v) ? v : [];
        } catch (e) {
          return [];
        }
      })();
  const isProtected = pagesList.includes(pageKey);
  if (!isProtected || unlocked) return children;

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    setChecking(true);
    try {
      await clientAccessAPI.verifyAccess(activeCompany.company_id, password);
      setUnlocked(true); // unlocks only this mounted instance of this page
    } catch (err) {
      setError(err.data?.message || "Incorrect password");
    } finally {
      setChecking(false);
    }
  };

  return (
    <div
      style={{
        minHeight: "60vh",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        padding: 28,
      }}
    >
      <div
        className="card"
        style={{
          maxWidth: 360,
          width: "100%",
          textAlign: "center",
          padding: 32,
        }}
      >
        <div
          style={{
            width: 52,
            height: 52,
            borderRadius: "50%",
            background: "var(--accent-dim)",
            border: "1px solid var(--accent)",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            margin: "0 auto 16px",
          }}
        >
          <Lock size={22} color="var(--accent)" />
        </div>
        <h3 style={{ fontSize: 16, fontWeight: 700, marginBottom: 4 }}>
          Protected Page
        </h3>
        <p style={{ fontSize: 13, color: "var(--text2)", marginBottom: 20 }}>
          Enter the access password provided by {activeCompany.company_name} to
          continue.
        </p>
        <form onSubmit={submit}>
          <input
            type="password"
            autoFocus
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            placeholder="Access password"
            style={{ marginBottom: 12, textAlign: "center" }}
          />
          {error && (
            <div
              style={{ color: "var(--danger)", fontSize: 12, marginBottom: 12 }}
            >
              {error}
            </div>
          )}
          <button
            type="submit"
            className="btn-primary"
            disabled={checking}
            style={{ width: "100%" }}
          >
            {checking ? "Verifying..." : "Unlock"}
          </button>
        </form>
      </div>
    </div>
  );
}
